import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { COMPANY, NAV } from "@/data/site";

export default function PublicFooter() {
  return (
    <footer className="bg-ei-navy text-ei-white/80 mt-auto" data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="font-display text-2xl font-bold text-ei-white tracking-tight">EI Battery Solutions</div>
            <p className="mt-4 text-sm leading-relaxed max-w-sm" data-testid="footer-eligibility">
              Wholesale supply of batteries, inverters, solar panels and accessories to registered electrical
              contractors, builders and property developers approved by EI Battery, across Australia. Supply only.
              Builders and developers must engage appropriately licensed electrical contractors and appropriately
              accredited installers. Trade-account and quotation approval required.
            </p>
            <p className="mt-4 text-xs text-ei-white/50">
              Business address: Kew VIC 3101. Service area: {COMPANY.serviceArea}.
              <br />ABN 23 943 384 114 — The Trustee for Yanakie Trust t/a EI Battery Solutions.
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="ei-eyebrow text-ei-gold">Explore</div>
            <ul className="mt-4 space-y-2 text-sm">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="hover:text-ei-white transition-colors" data-testid={`footer-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {n.label}
                  </Link>
                </li>
              ))}
              <li><Link to="/request-a-quote" className="hover:text-ei-white transition-colors">Request a quote</Link></li>
              <li><Link to="/claims" className="hover:text-ei-white transition-colors" data-testid="footer-claims">Warranty &amp; returns claims</Link></li>
              <li><Link to="/support" className="hover:text-ei-white transition-colors">Technical support</Link></li>
              <li><Link to="/support/fault-codes" className="hover:text-ei-white transition-colors">Fault code lookup</Link></li>
              <li><Link to="/guides" className="hover:text-ei-white transition-colors">Guides</Link></li>
              <li><Link to="/faq" className="hover:text-ei-white transition-colors">FAQ</Link></li>
            </ul>
            <div className="ei-eyebrow text-ei-gold mt-8">Catalogue</div>
            <ul className="mt-4 space-y-2 text-sm" data-testid="footer-catalogue-list">
              <li>
                <a href="/docs/ei-battery-catalogue.pdf" target="_blank" rel="noopener noreferrer"
                   className="hover:text-ei-gold" data-testid="footer-catalogue-download">
                  Download the wholesale catalogue (PDF)
                </a>
              </li>
              <li>
                <a href="/docs/ei-battery-dl-brochure.pdf" target="_blank" rel="noopener noreferrer"
                   className="hover:text-ei-gold" data-testid="footer-brochure-download">
                  Download the trade brochure (PDF)
                </a>
              </li>
              <li>
                <a href="/docs/ei-battery-shipping-information-and-freight-terms.pdf" target="_blank" rel="noopener noreferrer"
                   className="hover:text-ei-gold" data-testid="footer-shipping-terms-download">
                  Shipping information and freight terms (PDF)
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="ei-eyebrow text-ei-gold">Talk to sales</div>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 hover:text-ei-white" data-testid="footer-phone">
                  <Phone className="w-4 h-4 text-ei-gold" /> {COMPANY.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${COMPANY.email}`} className="inline-flex items-center gap-2 hover:text-ei-white" data-testid="footer-email">
                  <Mail className="w-4 h-4 text-ei-gold" /> {COMPANY.email}
                </a>
              </li>
              <li className="inline-flex items-start gap-2">
                <MapPin className="w-4 h-4 text-ei-gold mt-0.5" /> Australia VIC
              </li>
            </ul>
          </div>
        </div>

        <section className="mt-12 pt-8 border-t border-ei-white/10 text-center" aria-label="Company and payment information" data-testid="footer-supplied-graphics">
          <p className="ei-eyebrow text-ei-gold" data-testid="footer-company-marks-title">Company information</p>
          <div className="mt-5 mx-auto grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-10 items-center justify-items-center max-w-3xl" data-testid="footer-company-logos">
            <img src="/media/owner-supplied/trusted-trader-updated.png" alt="Australian Trusted Trader" width="318" height="159" className="w-full max-w-[210px] h-24 object-contain" loading="lazy" data-testid="footer-trusted-trader"/>
            <img src="/media/owner-supplied/australian-owned.png" alt="100% Australian owned and operated" width="447" height="447" className="w-28 h-28 object-contain justify-self-center" loading="lazy" data-testid="footer-australian-owned"/>
            <img src="/media/owner-supplied/asic.png" alt="Australian Securities and Investments Commission logo" width="371" height="164" className="w-full max-w-[230px] h-24 object-contain" loading="lazy" data-testid="footer-asic"/>
          </div>
          <div className="mt-8 mx-auto max-w-3xl" data-testid="footer-payment-information">
            <p className="ei-eyebrow text-ei-gold" data-testid="footer-payment-title">Payment information</p>
            <img src="/media/owner-supplied/payment-providers.png" alt="PayPal, Amazon Pay, Mastercard, Apple Pay, Visa, Stripe and Google Pay provider logos" width="721" height="83" className="mt-4 mx-auto w-full max-w-[721px] h-auto object-contain" loading="lazy" data-testid="footer-payment-provider-logos"/>
            <p className="mt-3 text-xs leading-relaxed text-ei-white/70" data-testid="footer-payment-availability">Card payments via Stripe only. Requesting a trade quote is always available.</p>
          </div>
        </section>

        <div className="mt-12 pt-6 border-t border-ei-white/10 flex flex-col sm:flex-row justify-between gap-4 text-xs text-ei-white/50">
          <div>© {new Date().getFullYear()} EI Battery Solutions. All rights reserved.</div>
          <div className="flex flex-wrap gap-4">
            <Link to="/policies/privacy" className="hover:text-ei-white" data-testid="footer-privacy">Privacy</Link>
            <Link to="/policies/terms" className="hover:text-ei-white" data-testid="footer-terms">Terms</Link>
            <Link to="/policies/delivery" className="hover:text-ei-white" data-testid="footer-delivery">Shipping &amp; delivery</Link>
            <Link to="/policies/warranty" className="hover:text-ei-white" data-testid="footer-warranty">Warranty</Link>
            <Link to="/policies/returns" className="hover:text-ei-white" data-testid="footer-returns">Returns</Link>
            <Link to="/policies/compliance" className="hover:text-ei-white" data-testid="footer-compliance">Service &amp; compliance</Link>
            <Link to="/staff/login" className="font-semibold text-ei-white/70 hover:text-ei-white" data-testid="footer-staff-login">Staff portal</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
